// VisualBankEditor — modo alternativo de edição do JSON do banco, focado em
// pequenos ajustes visuais que o admin precisa fazer com frequência:
//   - corrigir quebras de linha vindas do OCR/extração
//   - colar imagens dentro de alternativas ou do enunciado
//   - inserir equações matemáticas/químicas via MathLive (Word Equation-style)
//   - editar pequenos textos (tema, fonte, explicação, gabarito modelo)
//
// NÃO substitui a edição direta do JSON — é uma view paralela. O admin
// alterna entre "Editor visual" e "JSON" pelo seletor no topo do upload.
// Ambos refletem a MESMA fonte de verdade (`questoes[]`), que o caller mantém.
//
// MVP: cobre só múltipla escolha (mc). Para essay e composite o editor exibe
// um aviso e mantém os campos somente leitura — admin usa o JSON nesses casos.
//
// Admin-only. [[project-visual-bank-editor]]

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type {
  PreviewQuestion,
  PreviewOpcao,
  PreviewTabela
} from "@app/ui/question-preview";
import { QuestionPreview } from "@app/ui/question-preview";
import {
  ChevronLeft,
  ChevronRight,
  Crop,
  Eraser,
  ImageIcon,
  Plus,
  Sigma,
  Trash2,
  Upload,
  XCircle
} from "lucide-react";
import { MathInputModal } from "./math-input-modal";
import { ImageCropModal } from "./image-crop-modal";
import { QuestionRichField } from "@app/ui/question-rich-field";
import { flagEnabled } from "@app/ui/lib/feature-flags";

interface VisualBankEditorProps {
  questoes: PreviewQuestion[];
  onChange: (next: PreviewQuestion[]) => void;
}

// Helpers ---------------------------------------------------------------

function getOpcoes(q: PreviewQuestion): PreviewOpcao[] {
  if (Array.isArray(q.opcoes)) return q.opcoes;
  if (Array.isArray(q.alternativas)) return q.alternativas;
  return [];
}

function setOpcoes(q: PreviewQuestion, next: PreviewOpcao[]): PreviewQuestion {
  // Bug 2026-05-28: getOpcoes lê preferindo `opcoes`; antes setOpcoes escrevia
  // preferindo `alternativas`. Em bancos legados que têm OS DOIS campos
  // (alternativas + opcoes), edições caíam no `alternativas` mas o render
  // continuava mostrando o `opcoes` velho — usuário achava que nada salvava.
  // Solução: escrever em TODOS os campos presentes pra manter consistência,
  // criando `opcoes` se nenhum existir.
  const out: PreviewQuestion = { ...q };
  let touched = false;
  if (Array.isArray(q.opcoes)) {
    out.opcoes = next;
    touched = true;
  }
  if (Array.isArray(q.alternativas)) {
    out.alternativas = next;
    touched = true;
  }
  if (!touched) {
    out.opcoes = next;
  }
  return out;
}

function getOpcaoText(o: PreviewOpcao): string {
  return typeof o === "string" ? o : o.legenda ?? "";
}

function setOpcaoText(o: PreviewOpcao, text: string): PreviewOpcao {
  if (typeof o === "string") return text;
  return { ...o, legenda: text };
}

function trimLineBreaks(s: string): string {
  // Junta múltiplas quebras em uma só + remove espaços antes/depois de cada linha.
  return s
    .replace(/\r/g, "")
    .split("\n")
    .map((l) => l.replace(/[ \t]+/g, " ").trim())
    .filter((l, i, arr) => !(l === "" && arr[i - 1] === ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

function insertAtCursor(
  el: HTMLTextAreaElement | HTMLInputElement,
  insertion: string
): string {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const before = el.value.slice(0, start);
  const after = el.value.slice(end);
  const next = `${before}${insertion}${after}`;
  // Reposiciona o cursor logo após a inserção.
  const newPos = start + insertion.length;
  setTimeout(() => {
    el.focus();
    try {
      el.setSelectionRange(newPos, newPos);
    } catch {
      /* input inputs sem selectionRange */
    }
  }, 0);
  return next;
}

// Componente principal --------------------------------------------------

export function VisualBankEditor({ questoes, onChange }: VisualBankEditorProps) {
  const [idx, setIdx] = useState(0);
  const total = questoes.length;
  const safeIdx = total === 0 ? 0 : Math.min(Math.max(0, idx), total - 1);
  const q = (questoes[safeIdx] ?? {}) as PreviewQuestion;

  function patchQ(patch: Partial<PreviewQuestion>) {
    const next = questoes.slice();
    next[safeIdx] = { ...q, ...patch };
    onChange(next);
  }

  function patchOpcoes(next: PreviewOpcao[]) {
    const updated = questoes.slice();
    updated[safeIdx] = setOpcoes(q, next);
    onChange(updated);
  }

  function addQuestion() {
    const blank: PreviewQuestion = {
      tema: "",
      pergunta: "",
      alternativas: ["", "", "", "", ""],
      correta: 1
    };
    const next = questoes.slice();
    next.splice(safeIdx + 1, 0, blank);
    onChange(next);
    setIdx(safeIdx + 1);
  }

  function removeQuestion() {
    if (total === 0) return;
    const next = questoes.slice();
    next.splice(safeIdx, 1);
    onChange(next);
    setIdx(Math.max(0, safeIdx - 1));
  }

  if (total === 0) {
    return (
      <div class="space-y-3 rounded-md border border-dashed border-border bg-background/40 p-6 text-center text-sm text-muted-foreground">
        <p>Sem questões para editar.</p>
        <button
          type="button"
          onClick={addQuestion}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus className="size-3.5" /> Criar primeira questão
        </button>
      </div>
    );
  }

  const opcoes = getOpcoes(q);
  const tipo = q.tipo ?? (Array.isArray(q.items) ? "composite" : "mc");
  const supported = tipo === "mc" || opcoes.length > 0;

  return (
    <div class="space-y-4">
      {/* Nav + ações */}
      <header class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <button
          type="button"
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={safeIdx === 0}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="size-3" /> Anterior
        </button>
        <div class="inline-flex items-center gap-1 text-xs">
          <span class="text-muted-foreground">Questão</span>
          <input
            type="number"
            min={1}
            max={total}
            value={safeIdx + 1}
            onInput={(e) => {
              const n = Number((e.target as HTMLInputElement).value);
              if (Number.isFinite(n)) {
                setIdx(Math.min(Math.max(0, n - 1), total - 1));
              }
            }}
            aria-label="Ir para a questão"
            class="w-14 rounded-md border border-border bg-background px-1.5 py-0.5 text-center text-xs font-bold outline-none focus:border-primary"
          />
          <span class="text-muted-foreground">de {total}</span>
        </div>
        <button
          type="button"
          onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
          disabled={safeIdx >= total - 1}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          Próxima <ChevronRight className="size-3" />
        </button>

        <span class="ml-auto" />

        <button
          type="button"
          onClick={addQuestion}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent"
          title="Adicionar nova questão depois desta"
        >
          <Plus className="size-3" /> Nova
        </button>
        <button
          type="button"
          onClick={removeQuestion}
          class="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/5 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400"
          title="Excluir questão atual"
        >
          <Trash2 className="size-3" /> Remover
        </button>
      </header>

      {!supported && (
        <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          Esta questão é do tipo <strong>{tipo}</strong> — o editor visual hoje
          só cobre múltipla escolha (mc) por completo. Para editar este tipo,
          alterne para o modo <strong>JSON</strong> no topo.
        </div>
      )}

      {/* Form + preview lado a lado */}
      <div class="grid gap-4 lg:grid-cols-2">
        <div class="space-y-3">
          {/* Metadados */}
          <FormCard title="Metadados">
            <div class="grid grid-cols-2 gap-2">
              <FieldRow>
                <FieldLabel>Tema</FieldLabel>
                <input
                  type="text"
                  value={q.tema ?? ""}
                  onInput={(e) =>
                    patchQ({ tema: (e.target as HTMLInputElement).value })
                  }
                  placeholder="Ex: Cálculo I — Limites"
                  class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                />
              </FieldRow>
              <FieldRow>
                <FieldLabel>Subtema</FieldLabel>
                <input
                  type="text"
                  value={q.subtema ?? ""}
                  onInput={(e) =>
                    patchQ({ subtema: (e.target as HTMLInputElement).value })
                  }
                  placeholder="Ex: Limite no infinito"
                  class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                />
              </FieldRow>
            </div>
            <FieldRow>
              <FieldLabel>Fonte</FieldLabel>
              <input
                type="text"
                value={q.fonte ?? ""}
                onInput={(e) =>
                  patchQ({ fonte: (e.target as HTMLInputElement).value })
                }
                placeholder="Ex: ENEM 2024 / FUVEST 2023 Q12"
                class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
              />
            </FieldRow>
            <div class="grid grid-cols-2 gap-2">
              <FieldRow>
                <FieldLabel>Arquivo origem</FieldLabel>
                <input
                  type="text"
                  value={q.arquivo_origem ?? ""}
                  onInput={(e) =>
                    patchQ({
                      arquivo_origem: (e.target as HTMLInputElement).value
                    })
                  }
                  placeholder="Ex: enem-2024-dia2.pdf"
                  class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                />
              </FieldRow>
              <FieldRow>
                <FieldLabel>Nº no arquivo origem</FieldLabel>
                <input
                  type="text"
                  value={String(
                    q.numero_original ?? q.numero_origem ?? ""
                  )}
                  onInput={(e) =>
                    patchQ({
                      numero_original: (e.target as HTMLInputElement).value,
                      numero_origem: undefined
                    })
                  }
                  placeholder="Ex: 91, Q05a"
                  class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                />
              </FieldRow>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <FieldRow>
                <FieldLabel>Dificuldade (1–5)</FieldLabel>
                <DifficultyStars
                  value={q.dificuldade ?? 0}
                  onChange={(n) => patchQ({ dificuldade: n })}
                />
              </FieldRow>
              <FieldRow>
                <FieldLabel>Peso na prova</FieldLabel>
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  value={q.notaQuestao ?? 1}
                  onInput={(e) => {
                    const n = Number((e.target as HTMLInputElement).value);
                    patchQ({
                      notaQuestao: Number.isFinite(n) ? n : undefined
                    });
                  }}
                  aria-label="Peso da questão na prova agendada"
                  placeholder="1"
                  class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm tabular-nums outline-none focus:border-primary"
                />
              </FieldRow>
            </div>
            {/* Banca/Gênero/Linhas — só relevantes pra essay. Mostra
                sempre, mas claramente rotulados como opcionais. Mantém
                preenchidos quando admin vier de uma questão essay. */}
            {q.tipo === "essay" && (
              <div class="rounded-md border border-dashed border-amber-500/30 bg-amber-500/5 p-2 space-y-2">
                <p class="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                  Campos específicos de Essay
                </p>
                <div class="grid grid-cols-2 gap-2">
                  <FieldRow>
                    <FieldLabel>Banca</FieldLabel>
                    <input
                      type="text"
                      value={q.banca ?? ""}
                      onInput={(e) =>
                        patchQ({
                          banca: (e.target as HTMLInputElement).value
                        })
                      }
                      placeholder="Ex: FUVEST, ENEM"
                      class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                    />
                  </FieldRow>
                  <FieldRow>
                    <FieldLabel>Gênero</FieldLabel>
                    <input
                      type="text"
                      value={q.genero ?? ""}
                      onInput={(e) =>
                        patchQ({
                          genero: (e.target as HTMLInputElement).value
                        })
                      }
                      placeholder="Ex: dissertativo, narrativo"
                      class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                    />
                  </FieldRow>
                </div>
                <div class="grid grid-cols-3 gap-2">
                  <FieldRow>
                    <FieldLabel>Linhas mín</FieldLabel>
                    <input
                      type="number"
                      min={0}
                      value={q.linhas_min ?? ""}
                      onInput={(e) => {
                        const n = Number((e.target as HTMLInputElement).value);
                        patchQ({
                          linhas_min: Number.isFinite(n) ? n : undefined
                        });
                      }}
                      aria-label="Linhas mínimas"
                      placeholder="0"
                      class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm tabular-nums outline-none focus:border-primary"
                    />
                  </FieldRow>
                  <FieldRow>
                    <FieldLabel>Linhas máx</FieldLabel>
                    <input
                      type="number"
                      min={0}
                      value={q.linhas_max ?? ""}
                      onInput={(e) => {
                        const n = Number((e.target as HTMLInputElement).value);
                        patchQ({
                          linhas_max: Number.isFinite(n) ? n : undefined
                        });
                      }}
                      aria-label="Linhas máximas"
                      placeholder="30"
                      class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm tabular-nums outline-none focus:border-primary"
                    />
                  </FieldRow>
                  <FieldRow>
                    <FieldLabel>Nota máxima</FieldLabel>
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      value={q.nota_maxima ?? ""}
                      onInput={(e) => {
                        const n = Number((e.target as HTMLInputElement).value);
                        patchQ({
                          nota_maxima: Number.isFinite(n) ? n : undefined
                        });
                      }}
                      aria-label="Nota máxima"
                      placeholder="1000"
                      class="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm tabular-nums outline-none focus:border-primary"
                    />
                  </FieldRow>
                </div>
              </div>
            )}
          </FormCard>

          {/* Mídia da questão (q.imagens[] + q.tabelas[] que ficam fora do
              texto). Diferente das imagens INLINE coladas dentro de Caso/
              Enunciado (que viram markdown ![imagem](data:...)), essas aqui
              moram em campos JSON dedicados e renderizam em bloco antes do
              texto. Esta seção deixa o admin enxergar e editar elas
              explicitamente — antes ficavam invisíveis no editor.
              [[project-question-wysiwyg-editor]] */}
          <QuestionMediaSection q={q} patchQ={patchQ} />

          {/* Caso (texto-base) */}
          {(q.caso !== undefined || q.caso !== null) && (
            <FormCard title="Caso / texto-base (opcional)">
              <EditableTextarea
                value={q.caso ?? ""}
                onChange={(v) => patchQ({ caso: v })}
                rows={4}
                placeholder="Texto-base apresentado antes da pergunta (opcional)."
              />
            </FormCard>
          )}

          {/* Enunciado */}
          <FormCard title="Enunciado">
            <EditableTextarea
              value={q.pergunta ?? q.enunciado ?? ""}
              onChange={(v) => patchQ({ pergunta: v, enunciado: undefined })}
              rows={5}
              placeholder="Texto da pergunta."
            />
          </FormCard>

          {/* Alternativas */}
          {opcoes.length > 0 && (
            <FormCard title="Alternativas">
              <p class="mb-2 text-[10px] text-muted-foreground">
                Marque o radio para definir o gabarito. Anulada =
                <em> desmarcar todas</em> e setar correta=0 no JSON.
              </p>
              {opcoes.map((opt, i) => (
                <AlternativaRow
                  key={i}
                  index={i}
                  total={opcoes.length}
                  opt={opt}
                  correta={q.correta === i + 1}
                  onText={(text) => {
                    const next = opcoes.slice();
                    next[i] = setOpcaoText(opt, text);
                    patchOpcoes(next);
                  }}
                  onImage={(dataUrl, legenda) => {
                    const next = opcoes.slice();
                    next[i] = { imagem: dataUrl, legenda };
                    patchOpcoes(next);
                  }}
                  onClearImage={() => {
                    const next = opcoes.slice();
                    next[i] = "";
                    patchOpcoes(next);
                  }}
                  onMakeCorrect={() => patchQ({ correta: i + 1 })}
                  onRemove={() => {
                    const next = opcoes.slice();
                    next.splice(i, 1);
                    patchOpcoes(next);
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() => patchOpcoes([...opcoes, ""])}
                class="mt-2 inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent"
              >
                <Plus className="size-3" /> Alternativa
              </button>
            </FormCard>
          )}

          {/* Explicação */}
          <FormCard title="Explicação (opcional)">
            <EditableTextarea
              value={q.explicacao ?? ""}
              onChange={(v) => patchQ({ explicacao: v })}
              rows={4}
              placeholder="Resolução comentada apresentada na revisão."
            />
          </FormCard>
        </div>

        {/* Preview ao vivo */}
        <aside class="lg:sticky lg:top-4 lg:self-start">
          <div class="rounded-md border border-border bg-card/40 p-3">
            <header class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Como o aluno vê
            </header>
            <QuestionPreview q={q} showGabarito />
          </div>
        </aside>
      </div>
    </div>
  );
}

// Sub-componentes -------------------------------------------------------

function FormCard({ title, children }: { title: string; children: any }) {
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-3">
      <h4 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      {children}
    </section>
  );
}

function FieldRow({ children }: { children: any }) {
  return <div class="space-y-1">{children}</div>;
}

function FieldLabel({ children }: { children: any }) {
  return (
    <span class="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  );
}

interface EditableTextareaProps {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}

function EditableTextarea(props: EditableTextareaProps) {
  // WYSIWYG opcional atrás de feature flag `question_editor.wysiwyg`. Quando
  // ligada, troca o textarea pelo QuestionRichField — bidirecional com o
  // mesmo markdown canônico, então o caller continua salvando string MD.
  // Componentes separados pra não violar regra dos hooks ao trocar implementação.
  // [[project-question-wysiwyg-editor]]
  if (flagEnabled("question_editor.wysiwyg", false)) {
    return <EditableRichField {...props} />;
  }
  return <EditableTextareaLegacy {...props} />;
}

/** Wrapper do QuestionRichField que replica a UX do legado:
 *   - paste de imagem → insere `![imagem](dataUrl)` inline no markdown
 *   - clique numa imagem inline → abre crop modal e substitui no salvar
 *   - faixa visual abaixo do editor com thumbs das imagens detectadas no
 *     conteúdo + botão "Recortar" por imagem + empty state explícito
 *   - botão "Inserir imagem" pra adicionar do disco
 */
function EditableRichField({
  value,
  onChange,
  placeholder
}: EditableTextareaProps) {
  const [cropTarget, setCropTarget] = useState<
    | { src: string; apply: (next: string) => void }
    | null
  >(null);
  const inlineImages = useMemo(() => detectInlineImages(value), [value]);

  function pasteInlineImage(dataUrl: string) {
    // Abre o crop modal antes de inserir, igual ao legado, pra admin já
    // decidir o enquadramento.
    setCropTarget({
      src: dataUrl,
      apply: (next) => {
        onChange(`${value}\n\n![imagem](${next})\n`);
      }
    });
  }

  function clickImageToCrop(src: string, replace: (next: string) => void) {
    setCropTarget({ src, apply: (next) => replace(next) });
  }

  async function pickImageFromDisk() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = async () => {
      const f = input.files?.[0];
      input.remove();
      if (!f) return;
      try {
        const dataUrl = await blobToDataURL(f);
        pasteInlineImage(dataUrl);
      } catch {
        /* swallow */
      }
    };
    input.click();
  }

  function removeImage(url: string) {
    // Remove a sintaxe markdown `![alt](url)` ou tag <img src="url"> dessa
    // imagem específica no texto. Mantém o resto intocado.
    const escaped = url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const next = value
      .replace(new RegExp(`!\\[[^\\]]*\\]\\(${escaped}\\)`, "g"), "")
      .replace(new RegExp(`<img[^>]*src="${escaped}"[^>]*>`, "g"), "")
      .replace(/\n{3,}/g, "\n\n");
    onChange(next);
  }

  return (
    <>
      <QuestionRichField
        value={value}
        onChange={onChange}
        variant="rich"
        placeholder={placeholder}
        onPasteImageInline={pasteInlineImage}
        onImageClick={({ src, replace }) => clickImageToCrop(src, replace)}
        footerSlot={
          <div class="space-y-2 p-2">
            <div class="flex items-center justify-between gap-2">
              <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Imagens nesta seção ({inlineImages.length})
              </span>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => void pickImageFromDisk()}
                  class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[10px] font-semibold hover:bg-accent"
                  title="Adicionar imagem do disco"
                >
                  <ImageIcon className="size-3" /> Adicionar
                </button>
                <span class="text-[10px] text-muted-foreground italic">
                  ou cole com <kbd class="rounded border bg-background px-1">Ctrl+V</kbd>
                </span>
              </div>
            </div>
            {inlineImages.length === 0 ? (
              <div class="rounded-md border border-dashed border-border bg-background/40 px-3 py-3 text-center text-[11px] text-muted-foreground">
                Nenhuma imagem nesta seção. Cole uma com Ctrl+V dentro do
                editor ou clique em <strong>Adicionar</strong>.
              </div>
            ) : (
              <ul class="flex flex-wrap gap-2">
                {inlineImages.map((url, i) => (
                  <li
                    key={`${i}-${url.slice(0, 24)}`}
                    class="flex items-center gap-1.5 rounded-md border border-border bg-card p-1.5"
                  >
                    <img
                      src={url}
                      alt={`Imagem ${i + 1}`}
                      class="size-14 rounded border border-border object-cover"
                    />
                    <div class="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setCropTarget({
                            src: url,
                            apply: (next) =>
                              onChange(value.split(url).join(next))
                          })
                        }
                        class="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10"
                        title="Recortar/girar"
                      >
                        <Crop className="size-3" /> Recortar
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(url)}
                        class="inline-flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400"
                        title="Remover imagem"
                      >
                        <XCircle className="size-3" /> Remover
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        }
      />
      {cropTarget && (
        <ImageCropModal
          src={cropTarget.src}
          onCancel={() => setCropTarget(null)}
          onConfirm={(next) => {
            cropTarget.apply(next);
            setCropTarget(null);
          }}
        />
      )}
    </>
  );
}

function EditableTextareaLegacy({
  value,
  onChange,
  rows = 4,
  placeholder
}: EditableTextareaProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [mathOpen, setMathOpen] = useState(false);
  // Estado do crop modal: armazena a URL a recortar + um callback que aplica
  // o resultado de volta no texto (inserção nova vs substituição de existente).
  const [cropTarget, setCropTarget] = useState<
    | { src: string; apply: (next: string) => void }
    | null
  >(null);

  // Detecta todas as imagens inline (`![alt](data:image/...;base64,...)` e
  // `<img src="data:image/...">`) pra deixar o admin recortar uma por uma.
  // Limita ao protocolo `data:` pra não baixar URL externa em loop infinito.
  const inlineImages = useMemo(() => detectInlineImages(value), [value]);

  async function handlePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        if (!file) return;
        try {
          const dataUrl = await blobToDataURL(file);
          // Abre o modal de recorte; ao confirmar, insere markdown no cursor.
          setCropTarget({
            src: dataUrl,
            apply: (next) => {
              if (!ref.current) return;
              const md = `\n![imagem](${next})\n`;
              const text = insertAtCursor(ref.current, md);
              onChange(text);
            }
          });
        } catch {
          /* swallow */
        }
        return;
      }
    }
  }

  function handleTrim() {
    onChange(trimLineBreaks(value));
  }

  function handleMathInsert(latex: string, display: boolean) {
    if (!ref.current) {
      setMathOpen(false);
      return;
    }
    const wrapped = display ? `\n$$${latex}$$\n` : `$${latex}$`;
    const next = insertAtCursor(ref.current, wrapped);
    onChange(next);
    setMathOpen(false);
  }

  function cropExistingImage(originalUrl: string) {
    setCropTarget({
      src: originalUrl,
      apply: (nextUrl) => {
        // Substitui TODAS ocorrências da URL original — uma imagem colada
        // costuma aparecer só uma vez, e mesmo em raros casos de duplicata,
        // substituir todas é o comportamento esperado pelo admin.
        onChange(value.split(originalUrl).join(nextUrl));
      }
    });
  }

  return (
    <div class="space-y-1">
      <div class="flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => setMathOpen(true)}
          title="Inserir equação"
          aria-label="Inserir equação"
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[10px] font-semibold hover:bg-accent"
        >
          <Sigma className="size-3" /> Equação
        </button>
        <button
          type="button"
          onClick={handleTrim}
          title="Remover quebras de linha excessivas"
          aria-label="Remover quebras de linha"
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[10px] font-semibold hover:bg-accent"
        >
          <Eraser className="size-3" /> Limpar quebras
        </button>
        <span class="ml-auto text-[10px] text-muted-foreground">
          cole imagem aqui (Ctrl+V) — vira <code>![imagem](data:…)</code>
        </span>
      </div>
      <textarea
        ref={ref}
        value={value}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        onPaste={handlePaste as any}
        rows={rows}
        placeholder={placeholder}
        class="w-full resize-y rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
      />
      {inlineImages.length > 0 && (
        <div class="rounded-md border border-border bg-background/40 p-2">
          <div class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Imagens neste texto ({inlineImages.length})
          </div>
          <ul class="flex flex-wrap gap-2">
            {inlineImages.map((url, i) => (
              <li
                key={`${i}-${url.slice(0, 24)}`}
                class="flex items-center gap-1.5 rounded-md border border-border bg-card p-1.5"
              >
                <img
                  src={url}
                  alt={`Imagem ${i + 1}`}
                  class="size-12 rounded border border-border object-cover"
                />
                <button
                  type="button"
                  onClick={() => cropExistingImage(url)}
                  title="Recortar/girar esta imagem"
                  class="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10"
                >
                  <Crop className="size-3" /> Recortar
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {mathOpen && (
        <MathInputModal
          onCancel={() => setMathOpen(false)}
          onConfirm={handleMathInsert}
        />
      )}
      {cropTarget && (
        <ImageCropModal
          src={cropTarget.src}
          onCancel={() => setCropTarget(null)}
          onConfirm={(next) => {
            cropTarget.apply(next);
            setCropTarget(null);
          }}
        />
      )}
    </div>
  );
}

// Acha URLs `data:image/...;base64,...` referenciadas em markdown ou tags
// `<img src="...">` no texto. Deduplica preservando ordem (rara duplicata).
function detectInlineImages(text: string): string[] {
  if (!text) return [];
  const re = /data:image\/[a-zA-Z+]+;base64,[A-Za-z0-9+/=]+/g;
  const out: string[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (!seen.has(m[0])) {
      seen.add(m[0]);
      out.push(m[0]);
    }
  }
  return out;
}

interface AlternativaRowProps {
  index: number;
  total: number;
  opt: PreviewOpcao;
  correta: boolean;
  onText: (text: string) => void;
  onImage: (dataUrl: string, legenda: string) => void;
  onClearImage: () => void;
  onMakeCorrect: () => void;
  onRemove: () => void;
}

function AlternativaRow({
  index,
  total,
  opt,
  correta,
  onText,
  onImage,
  onClearImage,
  onMakeCorrect,
  onRemove
}: AlternativaRowProps) {
  const isImage = typeof opt !== "string" && !!opt.imagem;
  const letter = String.fromCharCode(65 + index);
  const [cropping, setCropping] = useState<string | null>(null);

  async function handlePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        if (!file) return;
        try {
          const dataUrl = await blobToDataURL(file);
          // Abre direto no crop modal pra admin já decidir o enquadramento.
          setCropping(dataUrl);
        } catch {
          /* swallow */
        }
        return;
      }
    }
  }

  async function handleFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await blobToDataURL(file);
      setCropping(dataUrl);
    } catch {
      /* swallow */
    } finally {
      // Limpa o input pra permitir escolher o mesmo arquivo novamente.
      input.value = "";
    }
  }

  return (
    <div
      class={`flex items-start gap-2 rounded-md border p-2 ${
        correta
          ? "border-emerald-500/50 bg-emerald-500/5"
          : "border-border bg-background/50"
      }`}
    >
      <label
        class="mt-1 inline-flex items-center gap-1 text-[11px] font-bold"
        title="Marcar como correta"
      >
        <input
          type="radio"
          name={`correta-${index}-${total}`}
          checked={correta}
          onChange={onMakeCorrect}
          class="size-3.5"
          aria-label={`Marcar alternativa ${letter} como correta`}
        />
        {letter}
      </label>
      <div class="min-w-0 flex-1 space-y-1">
        {isImage ? (
          <div class="space-y-1">
            <img
              src={(opt as { imagem: string }).imagem}
              alt={`Alternativa ${letter}`}
              class="max-h-32 rounded-md border border-border"
            />
            <input
              type="text"
              value={typeof opt !== "string" ? (opt.legenda ?? "") : ""}
              onInput={(e) =>
                onText((e.target as HTMLInputElement).value)
              }
              placeholder="Legenda da imagem (opcional)"
              class="w-full rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
            />
            <div class="flex flex-wrap items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  setCropping((opt as { imagem: string }).imagem)
                }
                class="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10"
                title="Recortar/girar esta imagem"
              >
                <Crop className="size-3" /> Recortar
              </button>
              <button
                type="button"
                onClick={onClearImage}
                class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[10px] font-semibold hover:bg-accent"
              >
                <XCircle className="size-3" /> Remover imagem
              </button>
            </div>
          </div>
        ) : flagEnabled("question_editor.wysiwyg", false) ? (
          <AlternativaWysiwygBody
            letter={letter}
            text={getOpcaoText(opt)}
            onText={onText}
            onImage={onImage}
            handleFile={handleFile}
          />
        ) : (
          <div class="flex items-start gap-1.5">
            <textarea
              value={getOpcaoText(opt)}
              onInput={(e) =>
                onText((e.target as HTMLTextAreaElement).value)
              }
              onPaste={handlePaste as any}
              rows={2}
              placeholder={`Alternativa ${letter}`}
              class="flex-1 resize-y rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
            />
            <label
              class="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
              title="Anexar imagem"
            >
              <ImageIcon className="size-3" />
              <input
                type="file"
                accept="image/*"
                onChange={handleFile}
                aria-label={`Anexar imagem na alternativa ${letter}`}
                class="hidden"
              />
            </label>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        title="Remover alternativa"
        aria-label="Remover alternativa"
        class="mt-1 rounded-md border border-red-500/40 bg-red-500/5 px-1.5 py-1 text-[10px] text-red-600 hover:bg-red-500/10 dark:text-red-400"
      >
        <Trash2 className="size-3" />
      </button>
      {cropping && (
        <ImageCropModal
          src={cropping}
          onCancel={() => setCropping(null)}
          onConfirm={(next) => {
            const legenda =
              typeof opt !== "string" ? (opt.legenda ?? "") : getOpcaoText(opt);
            onImage(next, legenda);
            setCropping(null);
          }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// DifficultyStars — pequeno picker 0–5 estrelas pra `q.dificuldade`. Clique
// na nº atual zera. [[project-question-wysiwyg-editor]]
// ===========================================================================

function DifficultyStars({
  value,
  onChange
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  const safe = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <div class="inline-flex items-center gap-0.5 rounded-md border border-border bg-background px-1.5 py-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === safe ? 0 : n)}
          aria-label={`Dificuldade ${n}`}
          title={`Definir dificuldade ${n}${n === safe ? " (clique pra zerar)" : ""}`}
          class={`text-base leading-none transition-colors ${
            n <= safe
              ? "text-amber-500 hover:text-amber-600"
              : "text-muted-foreground/40 hover:text-muted-foreground"
          }`}
        >
          ★
        </button>
      ))}
      {safe > 0 && (
        <button
          type="button"
          onClick={() => onChange(0)}
          title="Limpar dificuldade"
          aria-label="Limpar dificuldade"
          class="ml-1 text-[10px] text-muted-foreground hover:text-foreground"
        >
          limpar
        </button>
      )}
    </div>
  );
}

// ===========================================================================
// QuestionMediaSection — mostra imagens e tabelas EXTERNAS da questão
// (campos q.imagens[] e q.tabelas[], separados do texto). Visíveis e
// gerenciáveis com crop / remover / adicionar. [[project-question-wysiwyg-editor]]
// ===========================================================================

interface ExternalImage {
  url: string;
  legenda?: string;
}

function normalizeImagens(raw: PreviewQuestion["imagens"]): ExternalImage[] {
  if (!Array.isArray(raw)) return [];
  const out: ExternalImage[] = [];
  for (const it of raw) {
    if (typeof it === "string") out.push({ url: it });
    else if (it && typeof it === "object" && typeof it.url === "string") {
      out.push({ url: it.url, legenda: it.legenda });
    }
  }
  return out;
}

function denormalizeImagens(list: ExternalImage[]): PreviewQuestion["imagens"] {
  return list.map((i) => (i.legenda ? { url: i.url, legenda: i.legenda } : i.url));
}

function QuestionMediaSection({
  q,
  patchQ
}: {
  q: PreviewQuestion;
  patchQ: (p: Partial<PreviewQuestion>) => void;
}) {
  const imagens = useMemo(() => normalizeImagens(q.imagens), [q.imagens]);
  const tabelas = useMemo(() => q.tabelas ?? [], [q.tabelas]);

  const [cropTarget, setCropTarget] = useState<
    | { src: string; apply: (next: string) => void }
    | null
  >(null);
  const [editingTableIdx, setEditingTableIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pasteFocused, setPasteFocused] = useState(false);

  function updateImagens(next: ExternalImage[]) {
    patchQ({ imagens: denormalizeImagens(next) });
  }
  function updateTabelas(next: PreviewTabela[]) {
    patchQ({ tabelas: next });
  }

  async function addImageFromDisk() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = async () => {
      const f = input.files?.[0];
      input.remove();
      if (!f) return;
      await ingestImageFile(f);
    };
    input.click();
  }

  async function ingestImageFile(file: File) {
    if (!file.type.startsWith("image/")) return;
    try {
      const dataUrl = await blobToDataURL(file);
      setCropTarget({
        src: dataUrl,
        apply: (next) => updateImagens([...imagens, { url: next }])
      });
    } catch {
      /* swallow */
    }
  }

  function onDropZoneDragEnter(e: DragEvent) {
    if (!e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    setDragOver(true);
  }
  function onDropZoneDragOver(e: DragEvent) {
    if (!e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function onDropZoneDragLeave(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }
  async function onDropZoneDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    for (const f of files) {
      await ingestImageFile(f);
      // Só processa o primeiro pra evitar abrir N crop modals em cascata
      break;
    }
  }
  async function onDropZonePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of Array.from(items)) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        e.preventDefault();
        const f = it.getAsFile();
        if (f) await ingestImageFile(f);
        return;
      }
    }
  }

  function addTable() {
    const blank: PreviewTabela = {
      titulo: "",
      cabecalho: ["Col 1", "Col 2"],
      linhas: [["", ""]],
      nota: ""
    };
    updateTabelas([...tabelas, blank]);
    setEditingTableIdx(tabelas.length);
  }

  // Empty state: ainda assim mostra a seção pra admin saber que existe.
  const hasMidia = imagens.length > 0 || tabelas.length > 0;

  return (
    <FormCard title="Mídia da questão (imagens e tabelas)">
      {/* Imagens */}
      <div class="space-y-1.5">
        <div class="flex items-center justify-between">
          <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Imagens externas da questão ({imagens.length})
          </span>
          <button
            type="button"
            onClick={() => void addImageFromDisk()}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[10px] font-semibold hover:bg-accent"
          >
            <ImageIcon className="size-3" /> Escolher do disco
          </button>
        </div>

        {/* Drop zone bem demarcada — drag-and-drop + click pra escolher
            arquivo + foca pra capturar Ctrl+V. Sempre visível pra admin
            saber onde colar. [[project-question-wysiwyg-editor]] */}
        <button
          type="button"
          onClick={() => void addImageFromDisk()}
          onFocus={() => setPasteFocused(true)}
          onBlur={() => setPasteFocused(false)}
          onPaste={onDropZonePaste as unknown as preact.JSX.ClipboardEventHandler<HTMLButtonElement>}
          onDragEnter={onDropZoneDragEnter as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
          onDragOver={onDropZoneDragOver as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
          onDragLeave={onDropZoneDragLeave as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
          onDrop={onDropZoneDrop as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
          class={`relative flex w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-3 py-5 text-center text-[12px] font-medium transition-colors ${
            dragOver
              ? "border-primary bg-primary/10 text-primary"
              : pasteFocused
                ? "border-primary/60 bg-primary/5 text-foreground"
                : "border-border bg-background/40 text-muted-foreground hover:border-primary/60 hover:bg-primary/5 hover:text-foreground"
          }`}
        >
          <Upload className={`size-5 ${dragOver ? "text-primary" : "text-muted-foreground"}`} />
          <strong class="text-sm">
            {dragOver
              ? "Solte a imagem aqui"
              : "Arraste, clique ou cole imagem aqui"}
          </strong>
          <span class="text-[11px] text-muted-foreground">
            Drag-and-drop · clique pra escolher · <kbd class="rounded border bg-background px-1">Ctrl+V</kbd> {pasteFocused ? "(pode colar agora)" : "(clique aqui primeiro pra colar)"}
          </span>
          {!hasMidia && (
            <span class="mt-1 max-w-md text-[10px] italic text-muted-foreground">
              Imagens aqui renderizam em bloco antes do enunciado e ficam
              visíveis em todos os campos da questão.
            </span>
          )}
        </button>
        {imagens.length > 0 && (
          <ul class="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {imagens.map((im, i) => (
              <li
                key={`${i}-${im.url.slice(0, 24)}`}
                class="overflow-hidden rounded-md border border-border bg-card"
              >
                <img
                  src={im.url}
                  alt={im.legenda ?? `Imagem ${i + 1}`}
                  class="block aspect-video w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
                  onClick={() =>
                    setCropTarget({
                      src: im.url,
                      apply: (next) => {
                        const copy = imagens.slice();
                        copy[i] = { ...copy[i], url: next };
                        updateImagens(copy);
                      }
                    })
                  }
                  title="Clique para recortar/girar"
                />
                <div class="space-y-1 border-t border-border p-1.5">
                  <input
                    type="text"
                    value={im.legenda ?? ""}
                    onInput={(e) => {
                      const copy = imagens.slice();
                      copy[i] = {
                        ...copy[i],
                        legenda: (e.target as HTMLInputElement).value
                      };
                      updateImagens(copy);
                    }}
                    placeholder="Legenda (opcional)"
                    class="w-full rounded border border-border bg-background px-1.5 py-0.5 text-[10px]"
                  />
                  <div class="flex items-center justify-between gap-1">
                    <button
                      type="button"
                      onClick={() =>
                        setCropTarget({
                          src: im.url,
                          apply: (next) => {
                            const copy = imagens.slice();
                            copy[i] = { ...copy[i], url: next };
                            updateImagens(copy);
                          }
                        })
                      }
                      class="inline-flex items-center gap-1 rounded border border-primary/40 bg-primary/5 px-1.5 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10"
                      title="Recortar / girar"
                    >
                      <Crop className="size-3" /> Recortar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const copy = imagens.slice();
                        copy.splice(i, 1);
                        updateImagens(copy);
                      }}
                      class="inline-flex items-center gap-1 rounded border border-red-500/30 bg-red-500/5 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400"
                      title="Remover"
                    >
                      <XCircle className="size-3" /> Remover
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tabelas */}
      <div class="space-y-1.5">
        <div class="flex items-center justify-between">
          <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Tabelas externas ({tabelas.length})
          </span>
          <button
            type="button"
            onClick={addTable}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[10px] font-semibold hover:bg-accent"
          >
            <Plus className="size-3" /> Adicionar tabela
          </button>
        </div>
        {tabelas.length > 0 && (
          <ul class="space-y-2">
            {tabelas.map((t, i) => (
              <li
                key={i}
                class="rounded-md border border-border bg-card p-2 text-[11px]"
              >
                <div class="flex items-center justify-between gap-2">
                  <div class="min-w-0 flex-1">
                    <div class="truncate font-semibold">
                      {t.titulo || `Tabela ${i + 1}`}
                    </div>
                    <div class="text-muted-foreground">
                      {(t.cabecalho?.length ?? 0)} colunas × {(t.linhas?.length ?? 0)} linhas
                    </div>
                  </div>
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setEditingTableIdx(i)}
                      class="inline-flex items-center gap-1 rounded border border-primary/40 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const copy = tabelas.slice();
                        copy.splice(i, 1);
                        updateTabelas(copy);
                      }}
                      class="inline-flex items-center gap-1 rounded border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400"
                    >
                      <XCircle className="size-3" /> Remover
                    </button>
                  </div>
                </div>
                {/* Preview compacto da tabela */}
                {Array.isArray(t.cabecalho) && t.cabecalho.length > 0 && (
                  <table class="mt-2 w-full border-collapse text-[10px]">
                    <thead class="bg-muted/40">
                      <tr>
                        {t.cabecalho.map((h, hi) => (
                          <th key={hi} class="border border-border px-1.5 py-0.5 text-left font-semibold">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(t.linhas ?? []).map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci} class="border border-border px-1.5 py-0.5 text-muted-foreground">
                              {cell || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {cropTarget && (
        <ImageCropModal
          src={cropTarget.src}
          onCancel={() => setCropTarget(null)}
          onConfirm={(next) => {
            cropTarget.apply(next);
            setCropTarget(null);
          }}
        />
      )}

      {editingTableIdx !== null && tabelas[editingTableIdx] && (
        <TableEditModal
          table={tabelas[editingTableIdx]}
          onCancel={() => setEditingTableIdx(null)}
          onSave={(next) => {
            const copy = tabelas.slice();
            copy[editingTableIdx] = next;
            updateTabelas(copy);
            setEditingTableIdx(null);
          }}
        />
      )}
    </FormCard>
  );
}

// ===========================================================================
// TableEditModal — editor inline simples pra tabela externa.
// Adiciona/remove linhas e colunas. Edita células texto-livre.
// ===========================================================================

function TableEditModal({
  table,
  onCancel,
  onSave
}: {
  table: PreviewTabela;
  onCancel: () => void;
  onSave: (next: PreviewTabela) => void;
}) {
  const [draft, setDraft] = useState<PreviewTabela>(() => ({
    titulo: table.titulo ?? "",
    cabecalho: [...(table.cabecalho ?? [])],
    linhas: (table.linhas ?? []).map((r) => [...r]),
    nota: table.nota ?? ""
  }));

  const cols = draft.cabecalho?.length ?? 0;
  const rows = draft.linhas?.length ?? 0;

  function setHeader(ci: number, v: string) {
    const copy = (draft.cabecalho ?? []).slice();
    copy[ci] = v;
    setDraft({ ...draft, cabecalho: copy });
  }
  function setCell(ri: number, ci: number, v: string) {
    const copy = (draft.linhas ?? []).map((r) => [...r]);
    if (!copy[ri]) copy[ri] = [];
    copy[ri][ci] = v;
    setDraft({ ...draft, linhas: copy });
  }
  function addCol() {
    const nextH = [...(draft.cabecalho ?? []), `Col ${cols + 1}`];
    const nextRows = (draft.linhas ?? []).map((r) => [...r, ""]);
    setDraft({ ...draft, cabecalho: nextH, linhas: nextRows });
  }
  function removeCol(ci: number) {
    const nextH = (draft.cabecalho ?? []).filter((_, i) => i !== ci);
    const nextRows = (draft.linhas ?? []).map((r) =>
      r.filter((_, i) => i !== ci)
    );
    setDraft({ ...draft, cabecalho: nextH, linhas: nextRows });
  }
  function addRow() {
    const blank = (draft.cabecalho ?? []).map(() => "");
    setDraft({ ...draft, linhas: [...(draft.linhas ?? []), blank] });
  }
  function removeRow(ri: number) {
    setDraft({
      ...draft,
      linhas: (draft.linhas ?? []).filter((_, i) => i !== ri)
    });
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header class="flex items-center justify-between border-b px-5 py-3">
          <h2 class="text-base font-semibold">Editar tabela</h2>
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md p-1.5 hover:bg-muted"
            aria-label="Fechar"
          >
            <XCircle className="size-4" />
          </button>
        </header>

        <div class="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Título (opcional)
            </span>
            <input
              type="text"
              value={draft.titulo ?? ""}
              onInput={(e) =>
                setDraft({
                  ...draft,
                  titulo: (e.target as HTMLInputElement).value
                })
              }
              placeholder="Ex: Resultados do experimento"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            />
          </label>

          <div class="overflow-x-auto rounded-md border border-border">
            <table class="w-full text-xs">
              <thead class="bg-muted/40">
                <tr>
                  {(draft.cabecalho ?? []).map((h, ci) => (
                    <th key={ci} class="border-b border-border p-1">
                      <div class="flex items-center gap-1">
                        <input
                          type="text"
                          value={h}
                          onInput={(e) =>
                            setHeader(ci, (e.target as HTMLInputElement).value)
                          }
                          aria-label={`Cabeçalho da coluna ${ci + 1}`}
                          placeholder={`Col ${ci + 1}`}
                          class="w-full rounded border border-border bg-background px-1.5 py-0.5 text-xs font-semibold"
                        />
                        <button
                          type="button"
                          onClick={() => removeCol(ci)}
                          title="Remover coluna"
                          aria-label="Remover coluna"
                          class="rounded p-0.5 text-red-600 hover:bg-red-500/10 dark:text-red-400"
                        >
                          <XCircle className="size-3" />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th class="border-b border-border p-1">
                    <span class="sr-only">Ações</span>
                    <button
                      type="button"
                      onClick={addCol}
                      title="Adicionar coluna"
                      aria-label="Adicionar coluna"
                      class="inline-flex items-center gap-1 rounded border border-dashed border-border bg-background px-2 py-0.5 text-[10px] font-semibold hover:bg-accent"
                    >
                      <Plus className="size-3" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(draft.linhas ?? []).map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} class="border-b border-border p-1">
                        <input
                          type="text"
                          value={cell}
                          onInput={(e) =>
                            setCell(ri, ci, (e.target as HTMLInputElement).value)
                          }
                          aria-label={`Linha ${ri + 1}, coluna ${ci + 1}`}
                          placeholder=""
                          class="w-full rounded border border-border bg-background px-1.5 py-0.5 text-xs"
                        />
                      </td>
                    ))}
                    <td class="border-b border-border p-1">
                      <button
                        type="button"
                        onClick={() => removeRow(ri)}
                        title="Remover linha"
                        aria-label="Remover linha"
                        class="rounded p-0.5 text-red-600 hover:bg-red-500/10 dark:text-red-400"
                      >
                        <XCircle className="size-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div class="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              {cols} colunas × {rows} linhas
            </span>
            <button
              type="button"
              onClick={addRow}
              class="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
            >
              <Plus className="size-3" /> Adicionar linha
            </button>
          </div>

          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Nota / fonte (opcional)
            </span>
            <input
              type="text"
              value={draft.nota ?? ""}
              onInput={(e) =>
                setDraft({
                  ...draft,
                  nota: (e.target as HTMLInputElement).value
                })
              }
              placeholder="Ex: Fonte: IBGE, 2023"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            />
          </label>
        </div>

        <footer class="flex justify-end gap-2 border-t bg-muted/30 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md border bg-background px-4 py-1.5 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            class="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:brightness-110"
          >
            Salvar tabela
          </button>
        </footer>
      </div>
    </div>
  );
}

// ===========================================================================
// AlternativaWysiwygBody — corpo da alternativa no modo WYSIWYG. Tem o
// QuestionRichField + drop zone explícita pra adicionar imagem
// (drag/click/Ctrl+V). Cobre os 2 modelos do JSON:
//   - paste inline dentro do texto → vira ![imagem](data:...) na string
//   - clique/drag na drop zone abaixo → abre crop modal → vira opt={imagem,legenda}
// [[project-question-wysiwyg-editor]]
// ===========================================================================

function AlternativaWysiwygBody({
  letter,
  text,
  onText,
  onImage,
  handleFile
}: {
  letter: string;
  text: string;
  onText: (v: string) => void;
  onImage: (dataUrl: string, legenda: string) => void;
  handleFile: (e: Event) => Promise<void>;
}) {
  // Ref pro texto MAIS RECENTE — evita o problema de closure capturar texto
  // stale quando paste é assíncrono (FileReader) e o crop modal abre.
  // Sem isso, applyInlineImage usava o `text` da render onde o componente
  // foi montado, o que dava "nada acontece" pro user. [[project-question-wysiwyg-editor]]
  const latestText = useRef(text);
  useEffect(() => {
    latestText.current = text;
  }, [text]);

  const [dragOver, setDragOver] = useState(false);
  const [pasteFocused, setPasteFocused] = useState(false);
  // cropTarget agora pode ser:
  //  - { src, mode: "replace" } → drop zone/anexar substituirá o texto
  //  - { src, mode: "inline-recrop", replace } → user clicou numa imagem
  //    inline existente; replace é o callback que troca a URL no markdown
  const [cropTarget, setCropTarget] = useState<
    | { src: string; mode: "replace" }
    | {
        src: string;
        mode: "inline-recrop";
        replace: (nextSrc: string) => void;
        remove: () => void;
      }
    | null
  >(null);

  /** Paste inline: insere a imagem JÁ no markdown, sem passar por crop modal.
   *  User pode clicar na imagem inline pra recortar depois (onImageClick do
   *  QuestionRichField). Direto = previsível e não perde evento. */
  function pasteInlineDataUrl(dataUrl: string) {
    const current = latestText.current ?? "";
    const sep = current.length > 0 && !current.endsWith("\n") ? " " : "";
    onText(`${current}${sep}![imagem](${dataUrl}) `);
  }

  /** Click numa imagem inline existente → abre crop modal pra retrabalhar
   *  (recortar OU remover). [[bug-2026-05-28: imagens inline não saiam]] */
  function onInlineImageClick(params: {
    src: string;
    replace: (nextSrc: string) => void;
    remove: () => void;
  }) {
    setCropTarget({
      src: params.src,
      mode: "inline-recrop",
      replace: params.replace,
      remove: params.remove
    });
  }

  function applyReplaceImage(dataUrl: string) {
    // Modo "imagem substitui o texto da alternativa": vira opt={imagem,legenda}
    onImage(dataUrl, latestText.current ?? "");
  }

  async function pickFromDisk() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = async () => {
      const f = input.files?.[0];
      input.remove();
      if (!f) return;
      try {
        const dataUrl = await blobToDataURL(f);
        // Drop zone abaixo SEMPRE substitui o texto da alternativa.
        // Pra inserir inline no texto, user usa Ctrl+V dentro do campo.
        setCropTarget({ src: dataUrl, mode: "replace" });
      } catch {
        /* swallow */
      }
    };
    input.click();
  }

  function onZoneDragEnter(e: DragEvent) {
    if (!e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    setDragOver(true);
  }
  function onZoneDragOver(e: DragEvent) {
    if (!e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function onZoneDragLeave(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }
  async function onZoneDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f || !f.type.startsWith("image/")) return;
    try {
      const dataUrl = await blobToDataURL(f);
      setCropTarget({ src: dataUrl, mode: "replace" });
    } catch {
      /* swallow */
    }
  }
  async function onZonePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of Array.from(items)) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        e.preventDefault();
        const f = it.getAsFile();
        if (!f) return;
        try {
          const dataUrl = await blobToDataURL(f);
          setCropTarget({ src: dataUrl, mode: "replace" });
        } catch {
          /* swallow */
        }
        return;
      }
    }
  }

  return (
    <div class="flex flex-col gap-1.5">
      <div class="flex items-start gap-1.5">
        <div class="min-w-0 flex-1">
          <QuestionRichField
            value={text}
            onChange={onText}
            variant="inline"
            placeholder={`Alternativa ${letter}`}
            onPasteImageInline={pasteInlineDataUrl}
            onImageClick={onInlineImageClick}
          />
        </div>
        <label
          class="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
          title="Anexar imagem como substituto do texto"
        >
          <ImageIcon className="size-3" />
          <input
            type="file"
            accept="image/*"
            onChange={handleFile}
            aria-label={`Anexar imagem na alternativa ${letter}`}
            class="hidden"
          />
        </label>
      </div>

      {/* Drop zone explícita por alternativa — atalho visual pra colar/arrastar.
          Default mode = "replace" (imagem-como-alternativa, padrão tradicional).
          Botão pequeno ao lado pra inserir como inline no texto. */}
      <button
        type="button"
        onClick={() => void pickFromDisk()}
        onFocus={() => setPasteFocused(true)}
        onBlur={() => setPasteFocused(false)}
        onPaste={onZonePaste as unknown as preact.JSX.ClipboardEventHandler<HTMLButtonElement>}
        onDragEnter={onZoneDragEnter as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
        onDragOver={onZoneDragOver as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
        onDragLeave={onZoneDragLeave as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
        onDrop={onZoneDrop as unknown as preact.JSX.DragEventHandler<HTMLButtonElement>}
        class={`flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-2 py-1.5 text-[10px] transition-colors ${
          dragOver
            ? "border-primary bg-primary/10 text-primary"
            : pasteFocused
              ? "border-primary/60 bg-primary/5 text-foreground"
              : "border-border bg-background/40 text-muted-foreground hover:border-primary/60 hover:bg-primary/5"
        }`}
      >
        <Upload className="size-3" />
        <span>
          {dragOver
            ? "Solte para usar como alternativa"
            : "Arraste, clique ou cole imagem aqui"}
        </span>
        <kbd class="rounded border bg-background px-1 text-[9px]">Ctrl+V</kbd>
        {pasteFocused && (
          <span class="italic text-primary">(pronto pra colar)</span>
        )}
      </button>

      {cropTarget && (
        <ImageCropModal
          src={cropTarget.src}
          onCancel={() => setCropTarget(null)}
          onConfirm={(next) => {
            if (cropTarget.mode === "inline-recrop") {
              // Recortar imagem inline já existente: troca a URL no markdown
              cropTarget.replace(next);
            } else {
              applyReplaceImage(next);
            }
            setCropTarget(null);
          }}
          // Botão "Remover imagem" só faz sentido em inline-recrop — quando o
          // user clicou numa imagem JÁ existente no markdown da alternativa.
          // Pra imagem nova (mode="replace"), Cancelar já cobre.
          onRemove={
            cropTarget.mode === "inline-recrop"
              ? () => {
                  cropTarget.remove();
                  setCropTarget(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
